import * as React from 'react';
import { CacheProvider, type EmotionCache } from '@emotion/react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { captureFirstPageview, capturePageview, identifyUser, initAnalytics } from '@src/utility/analytics';
import { AppStateWrapper } from '@src/utility/state';
import { type NextComponentType, type NextPageContext } from 'next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import Router from 'next/router';
import { SessionProvider, useSession } from 'next-auth/react';

import createEmotionCache from '../src/createEmotionCache';
import theme from '../src/theme';

import '../styles/style.css';
import '../styles/swagger-ui.css';

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

export interface MyAppProps extends AppProps {
  Component: NextComponentType<NextPageContext> & { auth: boolean };
  emotionCache?: EmotionCache;
}

function Auth({ children }: { children: JSX.Element }): JSX.Element {
  const { status } = useSession({
    required: true,
    onUnauthenticated: () => {
      // we can try to store this inside the internal state and use useRouter to redirect
      const url = '/login';
      window.location.href = url;
    },
  });

  if (status === 'loading') {
    return (
      <Box sx={{ display: 'flex', width: '100%', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return children;
}

/**
 * SKD-2314: starts PostHog (production portal only), sends a page view with the route pattern
 * for the first page and every client-side navigation, and identifies the signed-in user.
 */
function Analytics(): null {
  const { data: session, status } = useSession();

  React.useEffect(() => {
    initAnalytics();
    // The singleton router, not useRouter(): the hook hands out a snapshot per render, so a
    // listener registered once would keep reading the first page's pathname.
    captureFirstPageview(Router.pathname);
    const onRouteChange = (): void => capturePageview(Router.pathname);
    Router.events.on('routeChangeComplete', onRouteChange);
    return () => Router.events.off('routeChangeComplete', onRouteChange);
  }, []);

  React.useEffect(() => {
    if (status === 'authenticated' && session?.user?.id) {
      identifyUser(session.user.id, session.user.email);
    }
  }, [status, session?.user?.id, session?.user?.email]);

  return null;
}

export default function MyApp(props: MyAppProps): JSX.Element {
  const {
    Component,
    emotionCache = clientSideEmotionCache,
    pageProps: { session, ...pageProps },
  } = props;
  return (
    <SessionProvider session={session}>
      <Analytics />
      <CacheProvider value={emotionCache}>
        <Head>
          <meta name="viewport" content="initial-scale=1, width=device-width" />
        </Head>
        <ThemeProvider theme={theme}>
          {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
          <CssBaseline />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <AppStateWrapper>
              {!Component.auth ? (
                <Component {...pageProps} />
              ) : (
                <Auth>
                  <Component {...pageProps} />
                </Auth>
              )}
            </AppStateWrapper>
          </LocalizationProvider>
        </ThemeProvider>
      </CacheProvider>
    </SessionProvider>
  );
}
